import { useNotifications } from 'core/notifications';
import useMutateArchiveProject from 'shared/api/projects/useMutateArchiveProject';
import useMutateConfidentialProject from 'shared/api/projects/useMutateConfidentialProject';
import useMutateDuplicateProject from 'shared/api/projects/useMutateDuplicateProject';
import useMutateFinalProject from 'shared/api/projects/useMutateFinalProject';
import useMutateUnarchiveProject from 'shared/api/projects/useMutateUnarchiveProject';
import useMutateUnconfidentialProject from 'shared/api/projects/useMutateUnconfidentialProject';
import useMutateUnfinalProject from 'shared/api/projects/useMutateUnfinalProject';
import useLatest from 'utils/hooks/useLatest';

const useSiteProjectsController = ({ projects, refetchProjects, onAllProjectsRemoved }) => {
  const { showErrorNotification, showSuccessNotification } = useNotifications();

  const projectsRef = useLatest(projects);

  const duplicateProjectMutation = useMutateDuplicateProject({
    onError: () => showErrorNotification({ description: 'Failed to duplicate the project' }),
    onSuccess: () => {
      refetchProjects();
      showSuccessNotification({ description: 'Project duplicated successfully' });
    }
  });

  const handleArchiveStatusChange = () => {
    if (projectsRef.current.length <= 1) {
      onAllProjectsRemoved?.();
    } else {
      refetchProjects();
    }
  };

  const archiveProjectMutation = useMutateArchiveProject({
    onError: () => showErrorNotification({ description: 'Failed to archive the project' }),
    onSuccess: () => {
      handleArchiveStatusChange();
      showSuccessNotification({ description: 'Project archived successfully' });
    }
  });

  const unarchiveProjectMutation = useMutateUnarchiveProject({
    onError: () => showErrorNotification({ description: 'Failed to unarchive the project' }),
    onSuccess: () => {
      handleArchiveStatusChange();
      showSuccessNotification({ description: 'Project unarchived successfully' });
    }
  });

  const confidentialProjectMutation = useMutateConfidentialProject({
    onError: () => showErrorNotification({ description: 'Failed to mark confidential' }),
    onSuccess: () => {
      refetchProjects();
      showSuccessNotification({ description: 'Marked project as confidential successfully' });
    }
  });

  const unconfidentialProjectMutation = useMutateUnconfidentialProject({
    onError: () => showErrorNotification({ description: 'Failed to remove confidential mark' }),
    onSuccess: () => {
      refetchProjects();
      showSuccessNotification({ description: 'Removed confidential successfully' });
    }
  });

  const finalProjectMutation = useMutateFinalProject({
    onError: () =>
      showErrorNotification({
        description: 'Failed to mark as final, please make sure there is only one final project per site'
      }),
    onSuccess: () => {
      refetchProjects();
      showSuccessNotification({ description: 'Marked project as final successfully' });
    }
  });

  const unfinalProjectMutation = useMutateUnfinalProject({
    onError: () => showErrorNotification({ description: 'Failed to remove final mark' }),
    onSuccess: () => {
      refetchProjects();
      showSuccessNotification({ description: 'Removed final successfully' });
    }
  });

  return {
    isDuplicateLoading: duplicateProjectMutation.isLoading,
    isArchiveLoading: archiveProjectMutation.isLoading || unarchiveProjectMutation.isLoading,
    isConfidentialLoading: confidentialProjectMutation.isLoading || unconfidentialProjectMutation.isLoading,
    isFinalLoading: finalProjectMutation.isLoading || unfinalProjectMutation.isLoading,
    duplicate: (id) => duplicateProjectMutation.mutate(id),
    archive: (id) => archiveProjectMutation.mutate(id),
    unarchive: (id) => unarchiveProjectMutation.mutate(id),
    confidential: (id) => confidentialProjectMutation.mutate(id),
    unconfidential: (id) => unconfidentialProjectMutation.mutate(id),
    final: (id) => finalProjectMutation.mutate(id),
    unfinal: (id) => unfinalProjectMutation.mutate(id)
  };
};

export default useSiteProjectsController;
